import { useEffect } from 'react'
import { usePostHog } from 'posthog-js/react'

/** Share of visitors whose whole session is recorded, by the page they land on. */
export const AUTH_PAGES_RECORDING_PERCENT = {
  signin: 5,
  signup: 100,
} as const

export type AuthPage = keyof typeof AUTH_PAGES_RECORDING_PERCENT

// Module state lives exactly as long as the page load. The page a load lands on decides, at its
// own rate, and the answer holds while the visitor moves between sign-in and sign-up.
let isPageLoadSampled: boolean | undefined

/**
 * Records the whole session of a sample of visitors to the sign-in and sign-up pages. Nothing
 * stops it, so the replay follows them into the app. Each page load decides afresh and gets a
 * replay of its own.
 */
export const useAuthPageRecording = (page: AuthPage): void => {
  const posthog = usePostHog()

  useEffect(() => {
    if (process.env.REACT_APP_ENVIRONMENT !== 'production') {
      return
    }
    // A popup this page opened (the ADP callback) belongs to the opener's visit, and starting a
    // session here would end the one the opener is recording.
    if (window.opener) {
      return
    }
    // Moving between sign-in and sign-up is the same visit, so it stays in the same replay.
    if (isPageLoadSampled !== undefined) {
      return
    }

    isPageLoadSampled = Math.random() * 100 < AUTH_PAGES_RECORDING_PERCENT[page]
    if (!isPageLoadSampled) {
      return
    }

    // PostHog keeps one session across reloads and tabs until it has been idle for 30 minutes,
    // which would append every reload to the same replay.
    posthog.sessionManager?.resetSessionId()
    // The sample is ours, so PostHog's replay sampling must not cut it down a second time.
    posthog.startSessionRecording({ sampling: true })
  }, [posthog, page])
}
